// Phase 4c — dashboard V-shape value types.
//
// `DashboardSpec` is the single new `Value` kind added to the engine
// (registered in `../../value.ts`'s union + `VALUE_KIND_TABLE`). The
// per-tile / per-sub-form descriptors below are NOT `Value` members —
// they are engine-internal tagged objects assembled by the dashboard
// scope's special-form ops (the same relationship `AggSpec` has to
// `aggregate`). Only `(dashboard …)` is callable at top level; it
// returns the lone `DashboardSpec` Value.
//
// V-shape (per HANDOFF + PHASE_4_PREP D1/D2): every column slot is an
// existing `ColumnRef` value — `ds` is a binding-name string or an
// inlined `LazyDataset`. No CDF traffic has happened when a
// `DashboardSpec` is produced; the renderer's per-tile resolution hook
// (4c.7) walks these `ColumnRef`s through the existing
// `resolveColumnRef` / materializer path on demand.
//
// `import type` only — keeps the runtime module graph acyclic
// (`../../value.ts` imports `DashboardSpec` from here at runtime for
// its `VALUE_KIND_TABLE` row; this file's reference to `ColumnRef` is
// erased).

import type { ColumnRef } from '../../value.ts';
import type {
  Align,
  AxisSide,
  Color,
  ColumnFormat,
  Curve,
  MetricVariant,
  NumFormat,
  Orientation,
  SeriesType,
  Stack,
  Stroke,
  XKind,
} from './enums.ts';

/**
 * Grid span `[width, height]` in 12-column units. Each component is an
 * integer in `[1, 12]` (validated by the tile ops, not here — this is
 * the carrier shape). Declaration order + span fully determines layout;
 * there is no per-tile position.
 */
export type Span = readonly [width: number, height: number];

/** The 12-column grid is fixed; `(dashboard …)` takes no kwargs. */
export const SPAN_RANGE = { min: 1, max: 12 } as const;

/**
 * One plotted series inside a `(cartesian …)` tile. `x` / `y` are
 * always explicit `(col …)` `ColumnRef`s (Phase 4c supports the
 * explicit form only — no positional dataset / bare-symbol shape).
 */
export type SeriesSpec = {
  readonly kind: 'series-spec';
  readonly type: SeriesType;
  readonly id: string;
  readonly label: string;
  readonly color: Color;
  readonly axis: AxisSide;
  readonly curve: Curve;
  readonly x: ColumnRef;
  readonly y: ColumnRef;
  readonly stroke?: Stroke;
};

/** A horizontal/vertical guide line inside a `(cartesian …)` tile. */
export type ReferenceLineSpec = {
  readonly kind: 'reference-line-spec';
  readonly axis: 'x' | 'y';
  readonly value: number;
  readonly label?: string;
  readonly style?: Stroke;
  readonly color?: Color;
};

/** A shaded band between two values inside a `(cartesian …)` tile. */
export type ReferenceAreaSpec = {
  readonly kind: 'reference-area-spec';
  readonly axis: 'x' | 'y';
  readonly start: number;
  readonly end: number;
  readonly label?: string;
  readonly color?: Color;
};

/**
 * A shaded min/max band inside a `(cartesian …)` tile. `lower` / `upper`
 * are explicit column refs plotted as a range area against the shared
 * `x` domain; independent of any series.
 */
export type BandSpec = {
  readonly kind: 'band-spec';
  readonly x: ColumnRef;
  readonly lower: ColumnRef;
  readonly upper: ColumnRef;
  readonly axis: AxisSide;
  readonly color?: Color;
  readonly label?: string;
};

/**
 * An x-axis configuration sub-form inside `(cartesian …)`. Carries the
 * axis label; folded into `CartesianSpec.xLabel` by the tile op.
 */
export type XAxisSpec = {
  readonly kind: 'x-axis-spec';
  readonly label?: string;
};

/**
 * A y-axis configuration sub-form inside `(cartesian …)`. `side`
 * selects the left or right axis; folded into
 * `CartesianSpec.yLabelLeft` / `yLabelRight` by the tile op.
 */
export type YAxisSpec = {
  readonly kind: 'y-axis-spec';
  readonly side: AxisSide;
  readonly label?: string;
};

/** A KPI metric tile. Phase 4c ships the `kpi` variant only. */
export type MetricSpec = {
  readonly kind: 'metric-spec';
  readonly variant: MetricVariant;
  readonly span: Span;
  readonly value: ColumnRef;
  readonly label: string;
  readonly format: NumFormat;
  readonly delta?: ColumnRef;
};

/** A cartesian (line/bar/area/dot) chart tile. */
export type CartesianSpec = {
  readonly kind: 'cartesian-spec';
  readonly span: Span;
  readonly xKind: XKind;
  readonly stack: Stack;
  readonly orientation: Orientation;
  readonly series: readonly SeriesSpec[];
  readonly referenceLines: readonly ReferenceLineSpec[];
  readonly referenceAreas: readonly ReferenceAreaSpec[];
  readonly bands: readonly BandSpec[];
  readonly xLabel?: string;
  readonly yLabelLeft?: string;
  readonly yLabelRight?: string;
};

/** One column inside a `(table …)` tile. */
export type ColumnSpec = {
  readonly kind: 'column-spec';
  readonly ref: ColumnRef;
  readonly label: string;
  readonly format: ColumnFormat;
  readonly align?: Align;
  readonly colorByValue?: boolean;
};

/** A tabular tile. */
export type TableSpec = {
  readonly kind: 'table-spec';
  readonly span: Span;
  readonly columns: readonly ColumnSpec[];
};

/**
 * A renderable tile. Discriminated by `kind`; the renderer's
 * `useResolveTile` switches over it (4c.7).
 */
export type TileSpec = MetricSpec | CartesianSpec | TableSpec;

/**
 * The single dashboard `Value` kind. Holds tiles in declaration order;
 * the renderer flows them left-to-right, top-to-bottom across the fixed
 * 12-column grid. Carries unresolved `ColumnRef`s (V-shape) — no I/O
 * has happened at construction time.
 *
 * A class (not a plain object) so `VALUE_KIND_TABLE`'s `instanceof`
 * discrimination in `../../value.ts` is unambiguous, mirroring
 * `ColumnRef` / `DescSpec`.
 */
export class DashboardSpec {
  readonly kind = 'dashboard-spec' as const;
  constructor(readonly tiles: readonly TileSpec[]) {}
}
